import { z } from 'zod'
import { LanguageCode } from './languages'
import { CurrencyCode } from './currency'
import { VatConfig } from './types'
import { RegionProviderSettings } from './schema'

export const DEFAULT_REGION = 'de' as const

export const RegionCodes = ['de', 'pl', 'it', 'es', 'at', 'kw'] as const

export type RegionCode = (typeof RegionCodes)[number]

export const RegionCode = Object.fromEntries(
  RegionCodes.map((code) => [code, code])
) as Record<RegionCode, RegionCode>

export type LinkedOrdersBehavior = 'primary-required' | 'combined'

export type AppRegion = {
  code: RegionCode
  name: string
  currency: CurrencyCode
  vat: VatConfig
  languages: {
    default: LanguageCode
    interface: LanguageCode[] // allowed languages to use in UI
  }
  payments: RegionProviderSettings
  // If set to false will skip validation of prepay methods based on store.prepay_method
  // Maybe we could move this to strapi?
  validatePrepayMethods: boolean
  linkedOrders?: {
    behavior: LinkedOrdersBehavior
  }
}

// This assumes we key the regions by their name
export type RegionMap = Record<RegionCode, AppRegion>

export const Regions: RegionMap = {
  de: {
    code: 'de',
    name: 'brillen.de',
    currency: 'EUR',
    vat: 'hidden',
    languages: {
      default: 'de',
      interface: ['de'],
    },
    validatePrepayMethods: true,
    payments: {
      terminal: ['stripe'],
      paylink: ['stripe'],
      invoice: ['payla'],
      monthly: ['unzer'],
      // Easy to test locally with cofidis in de region
      // monthly: ['cofidis'],
    },
  },
  pl: {
    code: 'pl',
    vat: 'hidden',
    name: 'brillen.pl',
    currency: 'PLN',
    languages: {
      default: 'pl',
      interface: ['pl'],
    },
    validatePrepayMethods: true,
    payments: {
      terminal: ['stripe'],
      paylink: ['stripe'],
      invoice: ['payla'],
      monthly: ['alior'],
    },
    linkedOrders: {
      behavior: 'combined',
    },
  },
  it: {
    code: 'it',
    name: 'occhiali24.it',
    currency: 'EUR',
    vat: 'required',
    languages: {
      default: 'it',
      interface: ['it'],
    },
    validatePrepayMethods: true,
    payments: {
      terminal: ['stripe'],
      paylink: ['stripe'],
      invoice: ['payla'],
      monthly: ['cofidis'],
    },
    linkedOrders: {
      behavior: 'combined',
    },
  },
  es: {
    code: 'es',
    name: 'gafas.es',
    currency: 'EUR',
    vat: 'optional',
    languages: {
      default: 'es',
      interface: ['es'],
    },
    validatePrepayMethods: true,
    payments: {
      terminal: ['stripe'],
      paylink: ['stripe'],
      invoice: ['payla'],
      monthly: ['cofidisApplication'],
    },
    linkedOrders: {
      behavior: 'combined',
    },
  },
  at: {
    code: 'at',
    name: 'brillen.at',
    currency: 'EUR',
    vat: 'hidden',
    languages: {
      default: 'de',
      interface: ['de'],
    },
    validatePrepayMethods: true,
    payments: {
      terminal: ['stripe'],
      paylink: ['stripe'],
      invoice: ['payla'],
      monthly: ['unzer'],
    },
  },
  kw: {
    code: 'kw',
    name: 'Steiner Vision Germany',
    currency: 'KWD',
    vat: 'hidden',
    languages: {
      default: 'en',
      interface: ['en'],
    },
    validatePrepayMethods: true,
    payments: {
      terminal: ['hasabe'],
      paylink: ['hasabe'],
      invoice: [],
      monthly: [],
    },
  },
}

export const regionNameSchema = z.enum(RegionCodes)

export const getCountryName = (country: string, locale: string) => {
  const names = new Intl.DisplayNames([locale], {
    type: 'region',
    style: 'long',
  })
  return names.of(country)
}

export const getRegion = (env?: string) => {
  const name = regionNameSchema.catch(DEFAULT_REGION).parse(env)

  return Regions[name]
}

export const RegionPrefixes: Record<RegionCode, string> = {
  de: '+49',
  pl: '+48',
  it: '+39',
  es: '+34',
  at: '+43',
  kw: '+965',
}
